import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { AppLayout } from "./components/Layout";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Rooms from "./pages/Rooms";
import RoomCalendar from "./pages/RoomCalendar";
import Guests from "./pages/Guests";
import Bookings from "./pages/Bookings";
import CheckInOut from "./pages/CheckInOut";
import Payments from "./pages/Payments";
import Invoices from "./pages/Invoices";
import OTAIntegration from "./pages/OTAIntegration";
import Menu from "./pages/Menu";
import Orders from "./pages/Orders";
import Reports from "./pages/Reports";
import Staff from "./pages/Staff";
import BedManagement from "./pages/BedManagement";
import Housekeeping from "./pages/Housekeeping";
import DocumentManagement from "./pages/DocumentManagement";
import NightAudit from "./pages/NightAudit";
import Coupons from "./pages/Coupons";
import DynamicPricing from "./pages/DynamicPricing";
import DirectBookings from "./pages/DirectBookings";

const routeConfig = [
  { path: "/", element: Dashboard },
  { path: "/rooms", element: Rooms },
  { path: "/room-calendar", element: RoomCalendar },
  { path: "/beds", element: BedManagement },
  { path: "/guests", element: Guests },
  { path: "/bookings", element: Bookings },
  { path: "/direct-bookings", element: DirectBookings },
  { path: "/check-in-out", element: CheckInOut },
  { path: "/payments", element: Payments },
  { path: "/invoices", element: Invoices },
  { path: "/ota-integration", element: OTAIntegration },
  { path: "/menu", element: Menu },
  { path: "/orders", element: Orders },
  { path: "/housekeeping", element: Housekeeping },
  { path: "/coupons", element: Coupons },
  { path: "/dynamic-pricing", element: DynamicPricing },
  { path: "/documents", element: DocumentManagement },
  { path: "/night-audit", element: NightAudit },
  { path: "/reports", element: Reports },
  { path: "/staff", element: Staff, adminOnly: true },
];

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;

  return <AppLayout>{children}</AppLayout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      {routeConfig.map(({ path, element: Page, adminOnly }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute adminOnly={adminOnly}>
              <Page />
            </ProtectedRoute>
          }
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" offset={72} richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
